import * as React from 'react';
import {
  mcDangerFilled,
  mcInfoFilled,
  mcSuccessFilled,
  mcWarningFilled,
} from '@mc/react-ui-icons';

import * as Classes from '../common/classes';
import { Icon } from '../icon/Icon';
import { InverseBgProvider } from '../theme/InverseBg';

import type { ToastIntent } from './Toast';

export const TOAST_KEY_CHARACTERS: string[] = (() => {
  const characters: string[] = [];
  for (let i = 0; i < 10; i++) {
    characters.push(i.toString());
  }
  for (let i = 0; i < 26; i++) {
    characters.push(String.fromCharCode(65 + i));
  }
  for (let i = 0; i < 26; i++) {
    characters.push(String.fromCharCode(97 + i));
  }
  return characters;
})();
export const TOAST_KEY_LENGTH = 6;
export const TOAST_TRANSITION_DURATION_MS = 250;

export type ToastManagerPosition =
  | 'top'
  | 'top-left'
  | 'top-right'
  | 'bottom'
  | 'bottom-left'
  | 'bottom-right';

export interface ToastConfig {
  timeout?: number;
}

export enum ToastTransitionPhase {
  FADE_IN = 'FADE_IN',
  NONE = 'NONE',
  FADE_OUT = 'FADE_OUT',
}

export interface ToastInfo {
  key: string;
  transitionPhase: ToastTransitionPhase;
  intent: ToastIntent;
  icon?: React.ReactNode;
  title?: React.ReactNode;
  message?: React.ReactNode;
  config?: ToastConfig;
}

export enum ToastAwaitTimeEnum {
  /** @deprecated */
  NORMAL = 8000,
  DEFAULT = 8000,
  INFO = 8000,
  SUCCESS = 8000,
  WARNING = 8000,
  ERROR = 12000,
}

export type ShowToastFn = (args: {
  intent: ToastIntent;
  icon?: React.ReactNode;
  title?: React.ReactNode;
  message?: React.ReactNode;
  inverseBg?: boolean;
  timeout?: number;
}) => string | null;

export type SetToastsFn = (update: (prev: ToastInfo[]) => ToastInfo[]) => void;

export type PushToastFn = (
  message?: React.ReactNode,
  config?: ToastConfig
) => string | null;

export type PushToastWithTitleFn = (
  title: React.ReactNode,
  message?: React.ReactNode,
  config?: ToastConfig
) => string | null;

export interface ToastStoreImplInitArg {
  getFn: () => ToastInfo[];
  setFn: SetToastsFn;
  setPositionFn: React.Dispatch<
    React.SetStateAction<ToastManagerPosition | null>
  >;
}

const generateKey = (): string => {
  let key = '';
  for (let i = 0; i < TOAST_KEY_LENGTH; i++) {
    key +=
      TOAST_KEY_CHARACTERS[
        Math.floor(Math.random() * TOAST_KEY_CHARACTERS.length)
      ];
  }
  return key;
};

const startFadeOut = (key: string): void => {
  _internalToastImpl.setFn((prev) =>
    prev.map((toast) =>
      toast.key !== key
        ? toast
        : {
            ...toast,
            transitionPhase: ToastTransitionPhase.FADE_OUT,
          }
    )
  );
};

const createMessageNode = ({
  title,
  message,
  inverseBg,
}: {
  title?: React.ReactNode;
  message?: React.ReactNode;
  inverseBg?: boolean;
}) => {
  return (
    <InverseBgProvider inverse={inverseBg}>
      <div className={Classes.TOAST_CONTENT}>
        {title && <div className={Classes.TOAST_TITLE}>{title}</div>}
        {message && <div className={Classes.TOAST_MESSAGE}>{message}</div>}
      </div>
    </InverseBgProvider>
  );
};

const close = (key: string): string | null => {
  _internalToastImpl.setFn((prev) => prev.filter((toast) => toast.key !== key));
  return null;
};

const stopFadeIn = (key: string): void => {
  _internalToastImpl.setFn((prev) =>
    prev.map((toast) =>
      toast.key !== key
        ? toast
        : {
            ...toast,
            transitionPhase: ToastTransitionPhase.NONE,
          }
    )
  );
};

const showToast: ShowToastFn = ({
  intent,
  icon,
  title,
  message,
  inverseBg,
  timeout,
}) => {
  const existingKeys = _internalToastImpl.getFn().map((toast) => toast.key);
  let key = generateKey();
  while (existingKeys.includes(key)) {
    key = generateKey();
  }
  _internalToastImpl.setFn((prev) => [
    ...prev,
    {
      key,
      transitionPhase:
        timeout === 0 ||
        (timeout && timeout >= TOAST_TRANSITION_DURATION_MS * 2)
          ? ToastTransitionPhase.FADE_IN
          : ToastTransitionPhase.NONE,
      intent,
      icon,
      message: createMessageNode({ title, message, inverseBg }),
    },
  ]);
  if (timeout && timeout >= TOAST_TRANSITION_DURATION_MS) {
    setTimeout(() => {
      startFadeOut(key);
    }, timeout - TOAST_TRANSITION_DURATION_MS);
  }
  if (
    timeout === 0 ||
    (timeout && timeout >= TOAST_TRANSITION_DURATION_MS * 2)
  ) {
    setTimeout(() => {
      stopFadeIn(key);
    }, TOAST_TRANSITION_DURATION_MS);
  }
  if (timeout && timeout > 0) {
    setTimeout(() => {
      close(key);
    }, timeout);
  }
  return key;
};

const _internalToastImpl: {
  _initialized: boolean;
} & ToastStoreImplInitArg = {
  _initialized: false,
  getFn: () => [],
  setFn: () => null,
  setPositionFn: () => null,
};

let _preparedPosition: ToastManagerPosition = 'top-right';

const initializePosition = (): void => {
  _internalToastImpl.setPositionFn(_preparedPosition);
};

export const initializeToastManager = (initArg: ToastStoreImplInitArg) => {
  _internalToastImpl._initialized = true;
  _internalToastImpl.getFn = initArg.getFn;
  _internalToastImpl.setFn = initArg.setFn;
  _internalToastImpl.setPositionFn = initArg.setPositionFn;
  initializePosition();
};

const closeToast = (key: string): string | null => {
  _internalToastImpl.setFn((prev) => prev.filter((toast) => toast.key !== key));
  return null;
};

const clearToasts = (): string[] => {
  const currentKeys = _internalToastImpl.getFn().map((toast) => toast.key);
  _internalToastImpl.setFn(() => []);
  return currentKeys;
};

/** @deprecated */
const normalWithTitle: PushToastWithTitleFn = (title, message, config) => {
  return showToast({
    intent: 'normal',
    icon: <Icon fixedWidth icon={mcInfoFilled} />,
    title,
    message,
    inverseBg: false,
    timeout: config?.timeout ?? ToastAwaitTimeEnum.NORMAL,
  });
};

const defaultWithTitle: PushToastWithTitleFn = (title, message, config) => {
  return showToast({
    intent: 'default',
    icon: <Icon fixedWidth icon={mcInfoFilled} />,
    title,
    message,
    inverseBg: false,
    timeout: config?.timeout ?? ToastAwaitTimeEnum.DEFAULT,
  });
};

const infoWithTitle: PushToastWithTitleFn = (title, message, config) => {
  return showToast({
    intent: 'info',
    icon: <Icon fixedWidth icon={mcInfoFilled} />,
    title,
    message,
    inverseBg: false,
    timeout: config?.timeout ?? ToastAwaitTimeEnum.INFO,
  });
};

const successWithTitle: PushToastWithTitleFn = (title, message, config) => {
  return showToast({
    intent: 'success',
    icon: <Icon fixedWidth icon={mcSuccessFilled} />,
    title,
    message,
    inverseBg: true,
    timeout: config?.timeout ?? ToastAwaitTimeEnum.SUCCESS,
  });
};

const warningWithTitle: PushToastWithTitleFn = (title, message, config) => {
  return showToast({
    intent: 'warning',
    icon: <Icon fixedWidth icon={mcWarningFilled} />,
    title,
    message,
    inverseBg: true,
    timeout: config?.timeout ?? ToastAwaitTimeEnum.WARNING,
  });
};

const errorWithTitle: PushToastWithTitleFn = (title, message, config) => {
  return showToast({
    intent: 'danger',
    icon: <Icon fixedWidth icon={mcDangerFilled} />,
    title,
    message,
    inverseBg: true,
    timeout: config?.timeout ?? ToastAwaitTimeEnum.ERROR,
  });
};

/** @deprecated */
const normal: PushToastFn = (message, config) => {
  return normalWithTitle(undefined, message, config);
};

const defaultToast: PushToastFn = (message, config) => {
  return defaultWithTitle(undefined, message, config);
};

const info: PushToastFn = (message, config) => {
  return infoWithTitle(undefined, message, config);
};

const success: PushToastFn = (message, config) => {
  return successWithTitle(undefined, message, config);
};

const warning: PushToastFn = (message, config) => {
  return warningWithTitle(undefined, message, config);
};

const error: PushToastFn = (message, config) => {
  return errorWithTitle(undefined, message, config);
};

export const toaster = {
  normal,
  default: defaultToast,
  info,
  success,
  warning,
  error,
  normalWithTitle,
  defaultWithTitle,
  infoWithTitle,
  successWithTitle,
  warningWithTitle,
  errorWithTitle,
  close: closeToast,
  clear: clearToasts,
};
